// Library methods
import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";
import Loader from "src/components/shared/loaders/Loader/Loader";
import BaselineExternalExamSummary from "./BaselineExternalExamSummary/BaselineExternalExamSummary";
import BaselineExamSummary from "./BaselineExamSummary/BaselineExamSummary";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import { useUpdateProgressionBaseline } from "src/services/exams/hooks/progression-analyses/useUpdateProgressionBaseline";
import { useProgressionExams } from "src/services/exams/hooks/useProgressionExams";
import { useReportSectionData } from "src/services/exams/hooks/report-sections/useReportSectionData";
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// API
import { getExternalExamById } from "src/services/exams/api";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Utils
import { isRetinaLogikExam } from "src/utils/exams/progressionAnalysisHelper";
import { sortByOldestDate } from "src/utils/date/dateHelper";

// Types
import {
  ProgressionExamSummaryType,
  SelectedProgressionExamType,
} from "src/services/exams/types";

type UpdateBaselineModalType = {
  open: boolean;
  onClose: () => void;
};

const UpdateBaselineModal = ({ open, onClose }: UpdateBaselineModalType) => {
  const { t } = useTranslation();
  const { getToken } = useTokenAccess();

  const { clinicInfo } = useClinicContext();
  const { patient, progressionAnalysis } = useContext(ReportContext);

  const { mutate: updateBaselineMutation } = useUpdateProgressionBaseline(
    clinicInfo?.id,
    patient?.id,
    progressionAnalysis?.id
  );

  const { data: examsForEye, isLoading: isLoadingExams } = useProgressionExams(
    clinicInfo?.id,
    patient?.id,
    progressionAnalysis?.eye
  );

  const { mutateAsync: fetchReportSection } = useReportSectionData(
    clinicInfo?.id
  );

  // Fetch exam details (FN, FP, MD, PSD, etc)
  const fetchExamDetail = useCallback(
    async (
      exam: ProgressionExamSummaryType
    ): Promise<SelectedProgressionExamType> => {
      const reportData = await fetchReportSection({
        patientId: exam.patientId,
        examId: exam.examId as string,
        sectionId: exam.id,
      });

      if (!reportData) return exam;

      return {
        ...exam,
        // stats stay as the backend sends them; getExamStats formats them for display
        reportData: JSON.parse(reportData),
      };
    },
    [fetchReportSection]
  );

  // Fetch exam details (FN, FP, MD, PSD, etc)
  const fetchExternalExamDetail = useCallback(
    async (
      exam: ProgressionExamSummaryType
    ): Promise<SelectedProgressionExamType> => {
      if (!clinicInfo?.id) return exam;

      const token = await getToken();
      const controller = new AbortController();
      const detail = await getExternalExamById(
        token,
        clinicInfo.id,
        exam.patientId,
        exam.id,
        controller.signal
      );

      return detail ? { ...exam, data: detail.data } : exam;
    },
    [clinicInfo?.id, getToken]
  );

  const fetchDetail = useCallback(
    (exam: ProgressionExamSummaryType) =>
      isRetinaLogikExam(exam)
        ? fetchExamDetail(exam)
        : fetchExternalExamDetail(exam),
    [fetchExamDetail, fetchExternalExamDetail]
  );

  // The backend requires the baseline set to combine to exactly 2 exams
  // (a GPA baseline pair) -- so this is a max-2 multi-select, not a
  // single choice. Detail is tracked separately from selection now that
  // every candidate's detail is prefetched (not just the selected ones).
  const [selectedExamIds, setSelectedExamIds] = useState<Array<string>>([]);
  const [examDetails, setExamDetails] = useState<
    Record<string, SelectedProgressionExamType>
  >({});
  const [loadingDetailIds, setLoadingDetailIds] = useState<Array<string>>([]);
  const selectedCount = selectedExamIds.length;

  // Only exams which exist on the analysis are displayed
  const sortedExams = useMemo(() => {
    const analysisExamIds = new Set(
      (progressionAnalysis?.exams ?? []).map((exam) => exam.id)
    );
    const analysisExternalExamIds = new Set(
      (progressionAnalysis?.externalExams ?? []).map((exam) => exam.id)
    );

    const analysisExams = (examsForEye ?? []).filter((exam) =>
      isRetinaLogikExam(exam)
        ? analysisExamIds.has(exam.examId as string)
        : analysisExternalExamIds.has(exam.id)
    );

    // oldest first, the order the baseline pair is usually picked from
    return sortByOldestDate(analysisExams, "completionDate");
  }, [
    examsForEye,
    progressionAnalysis?.exams,
    progressionAnalysis?.externalExams,
  ]);

  const sortedExamIdsKey = useMemo(
    () => sortedExams.map((exam) => exam.id).join(","),
    [sortedExams]
  );

  // Select any pre-selected exams
  useEffect(() => {
    if (!open || !examsForEye) return;

    const currentBaselineIds = examsForEye
      .filter((exam) =>
        isRetinaLogikExam(exam)
          ? progressionAnalysis?.baselineExamIds?.includes(
              exam.examId as string
            )
          : progressionAnalysis?.baselineExternalExamIds?.includes(exam.id)
      )
      .map((exam) => exam.id);

    setSelectedExamIds(currentBaselineIds);
    setExamDetails({});
    setLoadingDetailIds([]);
  }, [
    open,
    examsForEye,
    progressionAnalysis?.baselineExamIds,
    progressionAnalysis?.baselineExternalExamIds,
  ]);

  // Prefetch every exam currently on screen (not just a selected one) so
  // its card can show stats immediately instead of waiting for a click.
  useEffect(() => {
    if (!open) return;

    const examsNeedingDetail = sortedExams.filter(
      (exam) => !examDetails[exam.id] && !loadingDetailIds.includes(exam.id)
    );
    if (examsNeedingDetail.length === 0) return;

    let cancelled = false;
    setLoadingDetailIds((prev) => [
      ...prev,
      ...examsNeedingDetail.map((exam) => exam.id),
    ]);

    Promise.all(
      examsNeedingDetail.map(async (exam) => {
        try {
          const detail = await fetchDetail(exam);
          if (!cancelled) {
            setExamDetails((prev) => ({ ...prev, [exam.id]: detail }));
          }
        } catch (error) {
          // Skip this one -- its detail couldn't be fetched.
        } finally {
          if (!cancelled) {
            setLoadingDetailIds((prev) => prev.filter((id) => id !== exam.id));
          }
        }
      })
    );

    return () => {
      cancelled = true;
    };
    // sortedExams/fetchDetail intentionally omitted -- see sortedExamIdsKey above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sortedExamIdsKey]);

  // Replace the oldest selection if a 3rd checkbox is clicked
  const toggleExamSelection = useCallback((examId: string) => {
    setSelectedExamIds((prev) => {
      if (prev.includes(examId)) return prev.filter((id) => id !== examId);
      return [...prev, examId].slice(-2);
    });
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!progressionAnalysis?.id) return;

    setIsSubmitting(true);

    try {
      const selectedExams = (examsForEye ?? []).filter((exam) =>
        selectedExamIds.includes(exam.id)
      );

      updateBaselineMutation({
        baselineExamIds: selectedExams
          .filter(isRetinaLogikExam)
          .map((exam) => exam.examId as string),
        baselineExternalExamIds: selectedExams
          .filter((exam) => !isRetinaLogikExam(exam))
          .map((exam) => exam.id),
      });
      onClose();
    } catch (error) {
      // do nothing
    } finally {
      setIsSubmitting(false);
    }
  }, [
    progressionAnalysis?.id,
    examsForEye,
    selectedExamIds,
    updateBaselineMutation,
    onClose,
  ]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("Update Baseline")}
      maxWidth="sm"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            sx={ModalCustomCancel}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            sx={ModalCustomConfirm}
            disabled={
              isSubmitting || (selectedCount !== 0 && selectedCount !== 2)
            }
          >
            {t("Update")}
          </Button>
        </Grid>
      }
    >
      <Grid
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          padding: "16px",
        }}
      >
        <Alert
          variant="info"
          alertContent={t(
            "You must select 0 or 2 baselines to update this value."
          )}
        />

        {isLoadingExams ? (
          <Loader containerProps={{ height: "100px" }} />
        ) : (
          <Grid sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {sortedExams.map((exam) => {
              const isSelected = selectedExamIds.includes(exam.id);
              const isLoadingDetail = loadingDetailIds.includes(exam.id);
              const isDisabled = isLoadingDetail;

              return isRetinaLogikExam(exam) ? (
                <BaselineExamSummary
                  key={exam.id}
                  exam={exam}
                  examDetail={examDetails[exam.id]}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                  isLoadingDetail={isLoadingDetail}
                  onSelect={() => toggleExamSelection(exam.id)}
                />
              ) : (
                <BaselineExternalExamSummary
                  key={exam.id}
                  exam={exam}
                  examDetail={examDetails[exam.id]}
                  isSelected={isSelected}
                  isDisabled={isDisabled}
                  isLoadingDetail={isLoadingDetail}
                  onSelect={() => toggleExamSelection(exam.id)}
                />
              );
            })}
          </Grid>
        )}
      </Grid>
    </DefaultModal>
  );
};

export default UpdateBaselineModal;
